import * as React from "react";

interface Feature {
  name: boolean;
}

interface FeaturesContextValue {
  features: Record<string, Feature> | null;
  hasFeature: (key: string) => boolean;
}

export const FeaturesContext = React.createContext<FeaturesContextValue | null>(
  null,
);

export const useFeatures = () => {
  const contextValue: FeaturesContextValue | null =
    React.useContext(FeaturesContext);
  if (!contextValue)
    throw new Error("useFeature must be used within a FeaturesProvider");
  return contextValue;
};

export const FeaturesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [features, setFeatures] = React.useState(null);

  React.useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const response = await fetch("/features.json");
        const featureFlags = await response.json();
        setFeatures(featureFlags);
      } catch (err) {
        console.error("Failed to fetch feature flags: ", err);
      }
    };

    fetchFeatures();
  }, []);

  const hasFeature = (key: string) => {
    return Boolean(features?.[key]);
  };

  const contextValue = {
    features,
    hasFeature,
  };

  return (
    <FeaturesContext.Provider value={contextValue}>
      {children}
    </FeaturesContext.Provider>
  );
};
